import { useFormik } from 'formik'
import { Form, FormLabel, Spinner } from 'react-bootstrap'
import { RiCloseLine } from 'react-icons/ri'
import * as Yup from 'yup'
import CustomDropzone from '../CustomDropzone.jsx'
import { useMetaDataMutation } from '../../redux/api/pipelineApi.js'

const allowed = ['json', 'csv']

const allowedFileTypes = { 'text/csv': ['.csv'], 'application/json': ['.json'] }

// Validation Schema
const validationSchema = Yup.object().shape({
  file: Yup.mixed()
    .required('A file is required')
    .test('filePresent', 'A file is required', (value) => value !== null)
    .test('fileType', 'Only .json or .csv files are allowed', (value) => {
      if (!value) return false
      const ext = value.name.split('.').pop().toLowerCase()
      return allowed.includes(ext)
    }),
})

const UploadMetaData = ({ setStep }) => {
  const [metaData, { isLoading }] = useMetaDataMutation()

  const handleSubmit = async (values, { setFieldError, resetForm }) => {
    try {
      const data = await metaData(values.file).unwrap()
      localStorage.setItem('pipeline_token', data.token)
      resetForm()
      setStep((s) => s + 1)
    } catch (err) {
      setFieldError('form', err?.data?.error || err?.data?.message || 'An error occurred during upload')
    }
  }

  const formik = useFormik({
    initialValues: { file: null },
    validationSchema,
    onSubmit: handleSubmit,
  })

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Group>
        <FormLabel>Meta Data File</FormLabel>
        <CustomDropzone
          acceptedFileTypes={allowedFileTypes}
          isMultiple={false}
          onSubmit={(file) => {
            formik.setFieldValue('file', file)
            formik.setFieldTouched('file', true, false) // Add false to prevent validation
            formik.setErrors({ ...formik.errors, file: undefined }) // Clear file error
          }}
          loading={isLoading}
        />
        {formik.touched.file && formik.errors.file && (
          <div className='text-danger mt-1' style={{ fontSize: '0.875rem' }}>
            {formik.errors.file}
          </div>
        )}
      </Form.Group>

      {formik.values.file && <FileDisplay file={formik.values.file} onRemove={() => formik.setFieldValue('file', null)} disabled={isLoading} />}

      {formik.errors.form && <div className='text-danger mb-3'>{formik.errors.form}</div>}

      <button type='submit' className='common-btn w-100 py-2 mt-2' disabled={isLoading}>
        {isLoading ? (
          <div className='d-flex align-items-center justify-content-center gap-2'>
            <Spinner animation='border' size='sm' />
            Uploading...
          </div>
        ) : (
          'Upload'
        )}
      </button>
    </Form>
  )
}

export default UploadMetaData

const FileDisplay = ({ file, onRemove, disabled }) => (
  <div className='d-flex justify-content-between align-items-center p-3 rounded-3 my-2' style={{ backgroundColor: '#f6f6f6' }}>
    <div>
      <p className='fw-bold m-0' style={{ wordBreak: 'break-word' }}>
        {file.name}
      </p>
      <p className='text-secondary m-0'>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
    </div>
    <button className='border-0 rounded-circle bg-transparent' onClick={onRemove} disabled={disabled}>
      <RiCloseLine size={24} />
    </button>
  </div>
)
